import { PostgrestError } from "@supabase/supabase-js";
import { type ClassValue, clsx } from "clsx";
import { Ref } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function setRef<T>(ref: Ref<T>, target: T) {
  if (ref && typeof ref === "function") {
    ref(target);
  } else if (ref && "current" in ref) {
    ref.current = target;
  }
}

export function isPrimitive(value: unknown): boolean {
  return typeof value !== "object" && value != null;
}

export function isNullOrUndefined(value: unknown): boolean {
  return value === undefined || value === null;
}

export function size<T>(obj: T): number {
  if (isNullOrUndefined(obj)) return 0;

  if (Array.isArray(obj)) {
    return obj.length;
  }

  if (isPrimitive(obj)) {
    return 1;
  }

  return Object.keys(obj as object).length;
}

export function normalizeError(
  error: string | Error | PostgrestError | unknown | null,
) {
  if (!error) return { error: null };
  if (typeof error === "string") {
    return { error };
  }

  if (error instanceof Error) {
    return { error: error.message };
  }

  return {
    error: (error as PostgrestError).details ||
      (error as PostgrestError).message || "An unknown error occurred",
  };
}

function isFileLike(value: unknown): value is File | Blob {
  return value instanceof File || value instanceof Blob;
}

function hasFileLikeValue(value: unknown): boolean {
  if (isFileLike(value)) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasFileLikeValue(item));
  }

  if (value && typeof value === "object") {
    return Object.values(value).some((val) => hasFileLikeValue(val));
  }

  return false;
}

function appendToFormData(
  formData: FormData,
  key: string,
  value: unknown,
): void {
  if (isFileLike(value)) {
    formData.append(key, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (isFileLike(item)) {
        formData.append(key, item);
      } else if (item && typeof item === "object" && "file" in item) {
        const fileValue = (item as { file?: File | Blob }).file;
        if (fileValue) {
          formData.append(key, fileValue);
        }
      } else if (item && typeof item === "object") {
        appendToFormData(formData, `${key}[${index}]`, item);
      } else if (item != null) {
        formData.append(`${key}[${index}]`, String(item));
      }
    });
    return;
  }

  if (value && typeof value === "object") {
    if ("file" in value) {
      const fileValue = (value as { file?: File | Blob }).file;
      if (fileValue) {
        formData.append(key, fileValue);
        return;
      }
    }

    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      appendToFormData(formData, `${key}.${nestedKey}`, nestedValue);
    });
    return;
  }

  if (value != null) {
    formData.append(key, String(value));
  }
}

export function prepareRequestBody(data: Record<string, unknown>): {
  body: FormData | string;
  headers: HeadersInit;
} {
  const headers: HeadersInit = {};

  if (hasFileLikeValue(data)) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      appendToFormData(formData, key, value);
    });
    return { body: formData, headers };
  }

  return {
    body: JSON.stringify(data),
    headers: { ...headers, "Content-Type": "application/json" },
  };
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function uniqBy<T>(array: T[], key: keyof T): T[] {
  if (!Array.isArray(array) || array.length === 0) {
    return [];
  }

  const seen = new Set<unknown>();
  const result: T[] = [];

  for (const item of array) {
    if (item == null) {
      continue;
    }

    const keyValue = item[key];

    // Handle null/undefined key values
    if (keyValue == null) {
      // Only include one null/undefined entry
      if (!seen.has(null)) {
        seen.add(null);
        result.push(item);
      }
      continue;
    }

    // Use a string representation for complex values (objects, arrays)
    // to ensure proper comparison
    const identifier = typeof keyValue === "object"
      ? JSON.stringify(keyValue)
      : String(keyValue);

    if (!seen.has(identifier)) {
      seen.add(identifier);
      result.push(item);
    }
  }

  return result;
}

export function kebabCase(str: string) {
  if (typeof str !== "string") return str;

  return str
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function formatDuration(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];

  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(" ");
}
